import * as React from "react";
import apiGateway from "./apiGateway";
import {
  combineDecks,
  getDeckWithCollectionData,
  getNumberInCollection,
  summarizeDeck,
  summarizeMetagame,
} from "./logic";
import { useDispatch, useSelector } from "react-redux";
import { Form, Row, Col, Card, Table, OverlayTrigger } from "react-bootstrap";
import Decks from "./Components/Decks";
import Deck from "./Components/Deck";
import Pinnable from "./Components/Pinnable";
import MetagameSummary from "./Components/MetagameSummary";
import { reducedCollectionSelector } from "./redux/collectionSelectors";
import {
  isLoadingCollectionSelector,
  selectedCollectionNameSelector,
} from "./redux/collectionFeatureSelectors";
import { Deck as DeckData } from "./logic";
import { X } from "react-bootstrap-icons";
import {
  updateSelectedFormat,
  updateMaximumMissingCards,
} from "./redux/decksIOwnFeatureReducer";
import {
  selectedFormatSelector,
  includeLeaguesSelector,
  maximumMissingCardsSelector,
} from "./redux/decksIOwnFeatureSelectors";
import {
  daysAgoSelector,
  includeDateSelector,
} from "./redux/decksFeatureSelectors";
import { updateDaysAgo } from "./redux/decksFeatureReducer";
import { useAuth0 } from "@auth0/auth0-react";
import { CardDisplay } from "./Components/CardDisplay";

const DecksIOwnPage = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth0();
  const selectedFormat = useSelector(selectedFormatSelector);
  const includeLeagues = useSelector(includeLeaguesSelector);
  const maximumMissingCards = useSelector(maximumMissingCardsSelector);
  const startDate = useSelector(includeDateSelector);
  const daysAgo = useSelector(daysAgoSelector);
  const [decks, setDecks] = React.useState(new Array<DeckData>());
  const [originalDecks, setOriginalDecks] = React.useState(
    new Array<DeckData>()
  );
  const [otherSelectedCard, setOtherSelectedCard] = React.useState<
    string | undefined
  >();
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedDeck, setSelectedDeck] = React.useState<DeckData>();
  const [selectedCard, setSelectedCard] = React.useState<string>();
  const [metagame, setMetagame] = React.useState<
    Array<{ percentage: number; archetype: string; count: number }>
  >([]);
  const collection = useSelector(reducedCollectionSelector);
  const collectionName = useSelector(selectedCollectionNameSelector);
  const isLoadingCollection = useSelector(isLoadingCollectionSelector);
  const reducedCollection = useSelector(reducedCollectionSelector);

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const decks = await apiGateway.loadDecksByDate(
          selectedFormat.toLowerCase(),
          startDate
        );

        setOriginalDecks(decks);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [startDate, selectedFormat]);

  React.useEffect(() => {
    const decksWithCollection = originalDecks.map((d) =>
      getDeckWithCollectionData(d, collection)
    );

    const filteredDecks = decksWithCollection.filter(
      (d) =>
        d.totalCardsNeeded <= maximumMissingCards &&
        (includeLeagues || !d.event.toLowerCase().includes("league")) &&
        d.date >= startDate
    );

    const metagame = summarizeMetagame(filteredDecks);
    setMetagame(metagame);
    setDecks(filteredDecks);
  }, [
    collection,
    originalDecks,
    includeLeagues,
    maximumMissingCards,
    startDate,
  ]);

  const getQualifier = (maximumMissingCards: number) =>
    maximumMissingCards > 0 ? "(kinda)" : "";

  const neededCards = React.useMemo(() => {
    var cardsInDecks = combineDecks(decks.map(summarizeDeck));

    return cardsInDecks
      .map((c) => {
        const numberInCollection = getNumberInCollection(
          reducedCollection,
          c.name
        );
        return {
          name: c.name,
          maxcount: c.maxcount,
          numberNeeded: c.maxcount - numberInCollection,
          numberInCollection,
        };
      })
      .filter((c) => c.numberNeeded > 0);
  }, [decks, reducedCollection]);

  return (
    <div>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Filters</Card.Title>
              <Form.Label>From:</Form.Label>
              <Form.Control
                as="select"
                value={daysAgo.toString()}
                onChange={(e) => {
                  dispatch(updateDaysAgo(parseInt(e.target.value)));
                }}
              >
                <option value={7}>last 7 days</option>
                <option value={14}>last 14 days</option>
                <option value={30}>last 30 days</option>
                <option value={90}>last 90 days</option>
                <option value={365}>last 365 days</option>
              </Form.Control>
              <Form.Label>Format:</Form.Label>
              <Form.Control
                as="select"
                value={selectedFormat}
                onChange={(e) => {
                  setSelectedDeck(undefined);
                  setDecks([]);
                  dispatch(updateSelectedFormat(e.target.value));
                }}
              >
                <option value="modern">Modern</option>
                <option value="pauper">Pauper</option>
                <option value="legacy">Legacy</option>
                <option value="pioneer">Pioneer</option>
                <option value="standard">Standard</option>
                <option value="premodern">Premodern</option>
              </Form.Control>
              <Form.Label>Maximum Missing Cards:</Form.Label>
              <Form.Control
                as="select"
                value={maximumMissingCards}
                onChange={(e) => {
                  setSelectedDeck(undefined);
                  setDecks([]);
                  dispatch(
                    updateMaximumMissingCards(parseInt(e.target.value, 10))
                  );
                }}
              >
                <option>0</option>
                <option>1</option>
                <option>2</option>
                <option>3</option>
                <option>4</option>
                <option>5</option>
              </Form.Control>
            </Card.Body>
          </Card>
          <h1>{`${selectedFormat} Decks I Own ${getQualifier(
            maximumMissingCards
          )} in ${collectionName} from ${new Intl.DateTimeFormat("en").format(
            startDate
          )}`}</h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <MetagameSummary
            isLoading={isLoading}
            metagameSummary={metagame}
            format={selectedFormat}
            showFooter={true}
          />
          {!isLoading && neededCards.length > 0 && isAuthenticated && (
            <>
              <h2>Cards Needed</h2>
              <Table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Max Played</th>
                  </tr>
                </thead>
                <tbody>
                  {neededCards.map((c) => (
                    <tr
                      key={c.name}
                      className={
                        c.numberInCollection === 0 ? "missing" : "partial"
                      }
                      onMouseOver={() => setOtherSelectedCard(c.name)}
                    >
                      <td>
                        {otherSelectedCard === c.name ? (
                          <OverlayTrigger
                            trigger={["hover", "focus"]}
                            key={`popover${otherSelectedCard}`}
                            placement="right"
                            overlay={
                              <div>
                                <CardDisplay selectedCard={c.name} />
                              </div>
                            }
                          >
                            <span>{c.name}</span>
                          </OverlayTrigger>
                        ) : (
                          <span>{c.name}</span>
                        )}
                      </td>
                      <td>
                        {c.maxcount}

                        {c.numberNeeded > 0 && (
                          <span>
                            (-
                            {Math.min(c.numberNeeded, c.maxcount)})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
        <Col>
          <Decks
            isLoading={isLoading}
            decks={decks}
            onDeckSelected={(d) => setSelectedDeck(d)}
            selectedDeck={selectedDeck}
          />
        </Col>
        {selectedDeck && (
          <Col>
            <Pinnable>
              <div>
                <X
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedDeck(undefined)}
                />
              </div>
              <Deck
                deck={getDeckWithCollectionData(selectedDeck, collection)}
                format={selectedFormat}
                selectedCard={selectedCard}
                selectCard={(c) => setSelectedCard(c)}
                isSignedIn={true}
                isLoadingCollection={isLoadingCollection}
              />
            </Pinnable>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default DecksIOwnPage;
